import i18nFactory from '@yandex-int/i18n';
import { values } from 'lodash';

import { UrlHelpers } from '../../service/UrlHelpers';
import {
    AccessRights,
    APP_PREFIX,
    ClientProperties,
    LOG_PREFIX,
    OfficeContact,
    OfficeInfo,
    OperatorData
} from '../../typings/common';

import * as keyset from './footer.i18n';

const i18n = i18nFactory(keyset);

export enum ActionSection {
    stat = 'stat',
    additional = 'additional',
    payments = 'payments',
    lists = 'lists',
    agencyManagement = 'agencyManagement',
    management = 'management',
    access = 'access'
}

export interface Actions {
    title: string;
    links: Array<{
        text: string;
        href: string;
        isPureRoute: boolean;
        isBeta?: boolean;
    }>;
    isPureRoute?: boolean;
}

export function mapApiContact(
    contact: OfficeContact,
    defaultCity: string
): {
    name: string;
    tel?: string;
} {
    let phoneExtension;

    if (contact.phoneExtension) {
        phoneExtension = i18n('phone-extension', {
            phone: contact.phoneExtension
        });
    }

    return {
        name: contact.cityName || defaultCity,
        tel: [contact.phone, phoneExtension, contact.description]
            .filter(el => el)
            .join(' ')
    };
}

export function getContacts(
    officeInfo: OfficeInfo
): Array<
    | {
          name: string;
          tel?: string;
      }
    | undefined
> {
    return [
        officeInfo.cityContact &&
            mapApiContact(officeInfo.cityContact, i18n('moscow')),
        ...officeInfo.regionContacts.map((contact, index) =>
            mapApiContact(contact, index === 0 ? i18n('regions') : '')
        )
    ];
}

export function getActions(
    operator: OperatorData,
    client: ClientProperties,
    access: AccessRights
): Actions[] {
    const login = client.login;

    if (login === undefined) {
        return [];
    }

    const ClientID = client.ClientID;

    const agencyLogin =
        client.agencyInfo && client.agencyInfo.login
            ? client.agencyInfo.login
            : '';

    const actionsDictionary = {
        canTransferMoney: {
            place: 'payments',
            href: UrlHelpers.getDirectLink('transfer', {
                ulogin: login
            }),
            text: i18n('perenos-sredstv-s-kampanii')
        },
        canCheckCampaignsStats: {
            place: ActionSection.stat,
            href: UrlHelpers.getDirectLink('showCampStat', {
                stat_type: 'campdate',
                ulogin: login
            }),
            text: i18n('statistika-po-vsem-kampaniyam')
        },
        canViewOfflineReports: {
            place: ActionSection.stat,
            href: UrlHelpers.getDirectLink('newPdfReport', {
                ulogin: login
            }),
            text: i18n('zakaz-otchetov')
        },
        canManageRetargertingConditions: {
            place: ActionSection.lists,
            href: UrlHelpers.getDirectLink('showRetargetingCond', {
                ulogin: login
            }),
            text: i18n('usloviya-podbora-auditorii')
        },
        canSeeAgencyClients: {
            place: ActionSection.agencyManagement,
            href: UrlHelpers.getDirectLink('showClients', {
                ulogin: agencyLogin
            }),
            text: i18n('spisok-klientov-agentstva')
        },
        canSeeClientRepresentatives: {
            place: ActionSection.access,
            href: UrlHelpers.getDirectLink('showClReps', {
                ulogin: login
            }),
            text: i18n('zaregistrirovannye-predstaviteli')
        },
        canCopyCampaign: {
            place: ActionSection.management,
            href: UrlHelpers.getDirectLink('copyCamp'),
            text: i18n('skopirovat-kampaniyu')
        },
        canManageUsingCommander: {
            place: ActionSection.management,
            href: /commander/,
            text: i18n('kommander')
        },
        canManageUsingXLS: {
            place: ActionSection.management,
            href: UrlHelpers.getDirectLink('showExportedXlsList', {
                ulogin: login
            }),
            text: i18n('xls-xlsx')
        },
        canManageUsingApi: {
            place: ActionSection.management,
            href: UrlHelpers.getDirectLink('apiSettings', {
                ulogin: login
            }),
            text: i18n('api')
        },
        canManageFeeds: {
            place: ActionSection.lists,
            href: UrlHelpers.getDirectLink('showFeeds', {
                ulogin: login,
                retpath: UrlHelpers.getRetPath()
            }),
            text: i18n('fidy')
        },
        canManageCreatives: {
            place: ActionSection.lists,
            href: UrlHelpers.getDirectLink('showCreatives', {
                ulogin: login,
                retpath: UrlHelpers.getRetPath()
            }),
            text: i18n('kreativy-dlya-smart-bannerov')
        },
        canManageTurboLandings: {
            place: ActionSection.lists,
            href: UrlHelpers.getDirectLink('showTurboLandings', {
                ulogin: login,
                ClientID: String(ClientID)
            }),
            text: i18n('turbolandings')
        },
        canViewMobileApps: {
            place: ActionSection.lists,
            href: UrlHelpers.createNewDirectLink(
                UrlHelpers.createNewDirectUrlPathByParams(APP_PREFIX, 'list'),
                {
                    ulogin: login
                }
            ),
            text: i18n('mobile-apps')
        },
        // TODO: DIRECT-77361 Необходим номер кампании.
        // В большом директе брался номер первой кампании. Нужно чтобы подвал брал данные из стора.
        //  : {
        //     place: ActionSection.extension,
        //     href: UrlHelpers.getDirectLink('manageVCards', {
        //       cid: campaignId
        //     }),
        //     text: i18n('master-zapolneniya-virtualnyh-vizitok')
        // },
        canMonitorPhrasesPositions: {
            place: ActionSection.stat,
            href: UrlHelpers.getDirectLink('listWarnPlace', {
                ulogin: login,
                sort: 'addtime',
                reverse: '1',
                ws_place: '0',
                ws_time: '1d',
                ws_cid: '0',
                ws_done: '0',
                ws_client: login
            }),
            text: i18n('monitoring-izmeneniy-poziciy-po')
        },
        canSearchPhrasesDuplicates: {
            place: ActionSection.additional,
            href: UrlHelpers.getDirectLink('searchDoublesOrderPage', {
                ulogin: login,
                stat_type: 'campdate'
            }),
            text: i18n('poisk-dubley-v-klyuchevyh')
        },
        canEditInternationalization: {
            place: ActionSection.additional,
            href: UrlHelpers.getDirectLink('i18nTmplList'),
            text: i18n('interfeys-internacionalizacii')
        },
        canUploadDocuments: {
            place: ActionSection.additional,
            href: UrlHelpers.getDirectLink('showUploadModerationDocs', {
                ulogin: login
            }),
            text: i18n('zagruzit-dokumenty')
        },
        canViewUserLogs: {
            place: ActionSection.additional,
            href: UrlHelpers.createNewDirectLink(
                UrlHelpers.createNewDirectUrlPathByParams(LOG_PREFIX),
                {
                    ulogin: login
                }
            ),
            isPureRoute: true,
            text: i18n('istoria-izmenenii')
        }
    };

    const actions = {
        [ActionSection.stat]: {
            title: i18n('stat'),
            links: []
        },
        [ActionSection.additional]: {
            title: i18n('additional'),
            links: []
        },
        [ActionSection.payments]: {
            title: i18n('payments'),
            links: []
        },
        [ActionSection.lists]: {
            title: i18n('lists'),
            links: []
        },
        [ActionSection.agencyManagement]: {
            title: i18n('agency-management'),
            links: []
        },
        [ActionSection.management]: {
            title: i18n('management'),
            links: []
        },
        [ActionSection.access]: {
            title: i18n('access'),
            links: []
        }
    };

    Object.keys(actionsDictionary).forEach(key => {
        if (!access[key]) {
            return;
        }

        const action = actionsDictionary[key];

        actions[action.place].links.push(action);
    });

    return values(actions).filter(el => el.links.length);
}
